import { useMemo, useState } from "react";
import { CalendarDays, Home, Zap, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ShowingEventForHeatmap {
  scheduled_date: string;
  event_type: string;
}

interface ShowingHeatmapProps {
  events: ShowingEventForHeatmap[];
  primaryColor?: string | null;
}

function getMonthName(month: number): string {
  return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][month];
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

interface CalendarDay {
  date: Date;
  showings: number;
  openHouses: number;
}

export function ShowingHeatmap({ events, primaryColor }: ShowingHeatmapProps) {
  const markerColor = primaryColor ?? "#1b489b";
  // 0 = current 4-week window, -1 = 4 weeks back, -2 = 8 weeks back, etc.
  const [windowOffset, setWindowOffset] = useState(0);

  // Build a date map from ALL events (for dot rendering)
  const dateMap = useMemo(() => {
    const map = new Map<string, { showings: number; openHouses: number }>();
    for (const s of events) {
      const key = s.scheduled_date.split("T")[0];
      const existing = map.get(key) ?? { showings: 0, openHouses: 0 };
      if (s.event_type === "open_house") existing.openHouses++;
      else existing.showings++;
      map.set(key, existing);
    }
    return map;
  }, [events]);

  const { calendarWeeks, monthLabel, totalShowings, totalOpenHouses, busiestDay, maxCount, trendDiff, trendPct, prevTotal } =
    useMemo(() => {
      // End of the current week + offset * 28 days
      const today = new Date();
      const endOfWeek = new Date(today);
      endOfWeek.setDate(endOfWeek.getDate() + (6 - endOfWeek.getDay()) + windowOffset * 28);

      const startDate = new Date(endOfWeek);
      startDate.setDate(startDate.getDate() - 27);
      startDate.setDate(startDate.getDate() - startDate.getDay());

      // Build calendar grid
      const weeks: CalendarDay[][] = [];
      let cur = new Date(startDate);
      while (cur <= endOfWeek) {
        const week: CalendarDay[] = [];
        for (let d = 0; d < 7; d++) {
          const key = cur.toISOString().split("T")[0];
          const data = dateMap.get(key);
          week.push({ date: new Date(cur), showings: data?.showings ?? 0, openHouses: data?.openHouses ?? 0 });
          cur.setDate(cur.getDate() + 1);
        }
        weeks.push(week);
      }

      const label = `${getMonthName(startDate.getMonth())} ${startDate.getFullYear()}${
        endOfWeek.getMonth() !== startDate.getMonth()
          ? ` – ${getMonthName(endOfWeek.getMonth())} ${endOfWeek.getFullYear()}`
          : ""
      }`;

      // Stats: only count events within this window
      const winStartKey = startDate.toISOString().split("T")[0];
      const winEndKey = endOfWeek.toISOString().split("T")[0];

      let totalS = 0;
      let totalOH = 0;
      const dayOfWeekCounts = [0, 0, 0, 0, 0, 0, 0];
      let max = 0;

      for (const [key, v] of dateMap) {
        const cnt = v.showings + v.openHouses;
        if (cnt > max) max = cnt;
        if (key >= winStartKey && key <= winEndKey) {
          totalS += v.showings;
          totalOH += v.openHouses;
          const dow = new Date(key + "T12:00:00").getDay();
          dayOfWeekCounts[dow] += cnt;
        }
      }

      const busiestDayIdx = dayOfWeekCounts.indexOf(Math.max(...dayOfWeekCounts));

      // Trend: count events in the previous 4-week window for comparison
      const prevEndDate = new Date(startDate);
      prevEndDate.setDate(prevEndDate.getDate() - 1);
      const prevStartDate = new Date(prevEndDate);
      prevStartDate.setDate(prevStartDate.getDate() - 27);
      prevStartDate.setDate(prevStartDate.getDate() - prevStartDate.getDay());
      const prevStartKey = prevStartDate.toISOString().split("T")[0];
      const prevEndKey = prevEndDate.toISOString().split("T")[0];

      let prevTotal = 0;
      for (const [key, v] of dateMap) {
        if (key >= prevStartKey && key <= prevEndKey) prevTotal += v.showings + v.openHouses;
      }

      const currentTotal = totalS + totalOH;
      const trendDiff = currentTotal - prevTotal;
      const trendPct = prevTotal > 0 ? Math.round(Math.abs(trendDiff / prevTotal) * 100) : null;

      return {
        calendarWeeks: weeks,
        monthLabel: label,
        totalShowings: totalS,
        totalOpenHouses: totalOH,
        busiestDay: DAY_NAMES[busiestDayIdx],
        maxCount: max,
        trendDiff,
        trendPct,
        prevTotal,
      };
    }, [dateMap, windowOffset]);

  const canGoForward = windowOffset < 0;

  return (
    <div className="border rounded-xl bg-card p-5 flex flex-col gap-4 lg:flex-row lg:gap-8">
      {/* Calendar grid */}
      <div className="flex-shrink-0">
        {/* Navigation header */}
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-muted-foreground">{monthLabel}</p>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setWindowOffset(o => o - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setWindowOffset(o => o + 1)}
              disabled={!canGoForward}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Day labels */}
        <div className="flex gap-2 text-xs text-muted-foreground mb-1.5">
          {DAY_LABELS.map((d, i) => (
            <div key={i} className="w-8 h-5 flex items-center justify-center text-center">{d}</div>
          ))}
        </div>

        {/* Dot grid */}
        <div className="space-y-2">
          {calendarWeeks.map((week, wi) => (
            <div key={wi} className="flex gap-2">
              {week.map((day, di) => {
                const count = day.showings + day.openHouses;
                const opacity = count === 0
                  ? 0.06
                  : maxCount > 0
                  ? Math.min(0.2 + (count / maxCount) * 0.8, 1)
                  : 0.06;
                const isToday = day.date.toDateString() === new Date().toDateString();
                return (
                  <div
                    key={di}
                    className={`w-8 h-8 rounded-full transition-transform hover:scale-110 cursor-default${isToday ? " ring-2 ring-offset-1 ring-current" : ""}`}
                    style={{ backgroundColor: markerColor, opacity }}
                    title={
                      count > 0
                        ? `${day.date.toLocaleDateString()}: ${count} event${count > 1 ? "s" : ""}`
                        : day.date.toLocaleDateString()
                    }
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground">
          <span>Less</span>
          {[0.06, 0.2, 0.4, 0.7, 1].map((o, i) => (
            <div key={i} className="w-4 h-4 rounded-full" style={{ backgroundColor: markerColor, opacity: o }} />
          ))}
          <span>More</span>
        </div>
      </div>

      {/* Stat cards — 2x2 grid */}
      <div className="lg:flex-1 flex flex-col gap-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">This Period</p>
        <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-4 bg-muted/50 rounded-xl px-5 py-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${markerColor}18` }}
          >
            <CalendarDays className="h-5 w-5" style={{ color: markerColor }} />
          </div>
          <div>
            <p className="text-2xl font-bold leading-none">{totalShowings}</p>
            <p className="text-xs text-muted-foreground mt-1">Showings this period</p>
          </div>
        </div>

        <div className="flex items-center gap-4 bg-muted/50 rounded-xl px-5 py-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${markerColor}18` }}
          >
            <Home className="h-5 w-5" style={{ color: markerColor }} />
          </div>
          <div>
            <p className="text-2xl font-bold leading-none">{totalOpenHouses}</p>
            <p className="text-xs text-muted-foreground mt-1">Open Houses this period</p>
          </div>
        </div>

        <div className="flex items-center gap-4 bg-muted/50 rounded-xl px-5 py-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${markerColor}18` }}
          >
            <Zap className="h-5 w-5" style={{ color: markerColor }} />
          </div>
          <div>
            <p className="text-xl font-bold leading-none">
              {(totalShowings + totalOpenHouses) > 0 ? `${busiestDay}s` : "—"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Busiest day this period</p>
          </div>
        </div>

        <div className="flex items-center gap-4 bg-muted/50 rounded-xl px-5 py-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${markerColor}18` }}
          >
            {trendDiff > 0
              ? <TrendingUp className="h-5 w-5 text-green-500" />
              : trendDiff < 0
              ? <TrendingDown className="h-5 w-5 text-red-400" />
              : <Minus className="h-5 w-5" style={{ color: markerColor }} />
            }
          </div>
          <div>
            <p className="text-xl font-bold leading-none">
              {prevTotal === 0
                ? "—"
                : trendDiff === 0
                ? "Same"
                : `${trendDiff > 0 ? "+" : ""}${trendDiff}`}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {prevTotal === 0
                ? "No prior period data"
                : `vs prior 4 weeks${trendPct !== null ? ` (${trendDiff > 0 ? "+" : ""}${trendPct}%)` : ""}`}
            </p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
